/**
 * Shared pieces for the homepage activation experiences — analytics
 * helpers, attribution storage, and the "Imagine if Memory Lane
 * remembered all of this" transition panel shown after every demo.
 */
import React from "react";
import { Check, ArrowRight } from "lucide-react";
import { track, EVENTS } from "@/lib/analytics";

const LS_KEY = "ml_activation";

export function readAttribution() {
    try { return JSON.parse(window.localStorage.getItem(LS_KEY) || "null") || {}; } catch { return {}; }
}

function writeAttribution(patch) {
    try {
        const cur = readAttribution();
        window.localStorage.setItem(LS_KEY, JSON.stringify({ ...cur, ...patch, at: new Date().toISOString() }));
    } catch { /* storage unavailable */ }
}

export function trackCardClick(card) {
    const cur = readAttribution();
    const first = !cur.first_card;
    writeAttribution({ first_card: cur.first_card || card, last_card: card });
    track(EVENTS.ACTIVATION_CARD_CLICK, { card, first_card_of_visit: first });
}

export function trackStep(card, step) {
    track(EVENTS.ACTIVATION_STEP, { card, step });
}

export function trackCompleted(card) {
    const cur = readAttribution();
    const completed = Array.from(new Set([...(cur.completed || []), card]));
    writeAttribution({ completed });
    track(EVENTS.ACTIVATION_COMPLETED, { card });
}

// Sample Journey result — carried into the account so the roadmap marker
// is pre-placed after signup (demo continuity).
export function saveJourneyStage(stageKey) {
    writeAttribution({ journey_stage_key: stageKey });
}

export function goToSignup(card, onClose) {
    track(EVENTS.ACTIVATION_SIGNUP_CLICK, { card });
    writeAttribution({ signup_card: card });
    if (onClose) onClose();
    setTimeout(() => {
        const el = document.querySelector('[data-testid="hero-email-input"]');
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            setTimeout(() => el.focus({ preventScroll: true }), 600);
        }
    }, 150);
}

const REMEMBERS = [
    "Your loved one's journey",
    "Questions you've asked",
    "Caregiver wellbeing",
    "Helpful resources",
    "Appointment preparation",
    "Personalized roadmap",
    "One story shared with every provider",
];

export function TransitionPanel({ card, onClose, ctaLabel = "Create Your Free Account" }) {
    return (
        <div className="mt-6 rounded-3xl bg-teal-soft/40 border border-teal/20 p-6 md:p-7" data-testid="activation-transition">
            <h3 className="font-heading font-semibold text-xl md:text-2xl text-[#12776F]">
                Imagine if Memory Lane remembered all of this.
            </h3>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {REMEMBERS.map((r) => (
                    <li key={r} className="flex items-start gap-2 text-sm text-[#2A3330]">
                        <Check size={15} strokeWidth={2.4} className="text-teal-dark mt-0.5 shrink-0" /> {r}
                    </li>
                ))}
            </ul>
            <p className="mt-4 text-sm text-[#4C5551] leading-relaxed">
                Memory Lane remembers your family&apos;s story so you don&apos;t have to start over at every appointment.
            </p>
            <button
                type="button"
                onClick={() => goToSignup(card, onClose)}
                data-testid="activation-signup-cta"
                className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-teal-dark text-white font-semibold px-7 py-3.5 text-sm md:text-base hover:bg-teal transition-colors shadow-sm"
            >
                {ctaLabel} <ArrowRight size={16} strokeWidth={2} />
            </button>
        </div>
    );
}

export function DemoBadge({ children = "Demo — nothing is saved, no account needed" }) {
    return (
        <p className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-semibold text-teal-dark bg-teal-soft/60 rounded-2xl px-3 py-1">
            {children}
        </p>
    );
}

export function OptionButton({ onClick, selected, children, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`text-left w-full rounded-2xl border px-4 py-3 text-sm transition-all ${selected
                ? "border-teal-dark bg-teal-soft/50 text-[#1F2624] font-medium"
                : "border-[#E2E8F0] bg-white text-[#2A3330] hover:border-teal/50 hover:bg-cream-light"}`}
        >
            {children}
        </button>
    );
}
